import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  clearAuthFlowMarker,
  clearAuthRedirectFlags,
  clearSignedOutAt,
  consumeIntendedRoute,
  hasFreshAuthFlowMarker,
  isSignedOutMarkerActive,
  peekIntendedRoute
} from '../utils/authFlow';
import { resolveAccessState } from '../utils/accessState';
import useSEO from '../hooks/useSEO';
import './platform/platform.css';
import './auth-transition-folio.css';

const AUTHENTICATED_HOME_ROUTE = '/home';

console.log('[AuthCallback] Component rendering...');

const AuthCallback = () => {
  console.log('[AuthCallback] Component function executing...');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redirected, setRedirected] = useState(false);
  const [statusStep, setStatusStep] = useState(0);
  const navigate = useNavigate();
  const { fetchUserSubscription, refreshProfileCache, markCallbackHydration } = useAuth();

  const STATUS_MESSAGES = [
    'Verifying identity...',
    'Loading your profile...',
    'Almost ready...',
  ];

  // Cycle through status messages while loading
  useEffect(() => {
    if (!loading) return;
    const interval = setInterval(() => {
      setStatusStep((prev) => (prev < STATUS_MESSAGES.length - 1 ? prev + 1 : prev));
    }, 1200);
    return () => clearInterval(interval);
  }, [loading]);

  useSEO({
    title: 'Authenticating | APDoneRight',
    description: 'Completing sign-in and setting up your APDoneRight session.',
    canonical: '/auth/callback',
    robots: 'noindex,follow',
  });

  useEffect(() => {
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const isTransientAuthError = (error) => {
      const status = typeof error?.status === 'number' ? error.status : null;
      if (status === 429 || status >= 500) return true;

      const code = (error?.code || '').toLowerCase();
      if (code === 'invalid_grant' || code === 'invalid_request' || code === 'otp_expired') {
        return false;
      }

      const message = (error?.message || '').toLowerCase();
      if (!message) return false;
      if (message.includes('invalid_grant') || message.includes('invalid request') || message.includes('invalid_request')) {
        return false;
      }
      if (message.includes('expired')) return false;

      return message.includes('network') ||
        message.includes('timeout') ||
        message.includes('temporarily') ||
        message.includes('rate limit') ||
        message.includes('server error') ||
        message.includes('429');
    };

    const exchangeCodeForSessionWithRetry = async (authCode) => {
      const maxAttempts = 3;
      const baseDelayMs = 200;

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        const result = await supabase.auth.exchangeCodeForSession(authCode);
        if (!result.error) {
          return result;
        }

        const shouldRetry = isTransientAuthError(result.error);
        if (!shouldRetry || attempt === maxAttempts) {
          return result;
        }

        const delayMs = baseDelayMs * Math.pow(3, attempt - 1);
        if (window.__authCallbackDebug) {
          if (!window.__authCallbackDebug.codeExchangeRetries) {
            window.__authCallbackDebug.codeExchangeRetries = [];
          }
          window.__authCallbackDebug.codeExchangeRetries.push({
            attempt,
            delayMs,
            status: result.error.status,
            code: result.error.code,
            message: result.error.message
          });
        }
        await sleep(delayMs);
      }

      return { data: null, error: new Error('Failed to establish your session. Please try again.') };
    };

    const handleAuthCallback = async () => {
      try {
        // Prevent infinite redirect loops
        if (redirected) {
          console.log('[AuthCallback] Already redirected, skipping...');
          return;
        }

        setLoading(true);
        console.log('[AuthCallback] Starting auth callback handler...');

        // Add a window-level debug property for browser console debugging
        window.__authCallbackDebug = {
          stage: 'starting',
          timestamp: new Date().toISOString()
        };

        // Clear any auth flags that might be set
        window.__AUTH_CHECK_IN_PROGRESS = false;

        // Clear intentional redirect flag and auth progress
        clearAuthRedirectFlags();

        // Check if this is a post-payment authentication
        const paymentCompleted = localStorage.getItem('paymentCompleted');
        const storedSubscriptionType = localStorage.getItem('subscriptionType') || 'basic';

        window.__authCallbackDebug.paymentFlags = { paymentCompleted, storedSubscriptionType };

        // First try to get session from URL
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        // Check URL for auth callback data
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');

        // Log what type of auth flow we're dealing with
        const authDetails = {
          hasCode: !!code,
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken
        };
        console.log('[AuthCallback] Auth flow details:', authDetails);
        window.__authCallbackDebug.authDetails = authDetails;
        window.__authCallbackDebug.stage = 'checking_auth_type';

        window.__authCallbackDebug.stage = 'obtaining_session';
        let session = null;

        if (code) {
          console.log('[AuthCallback] Auth with code flow detected');
          window.__authCallbackDebug.authMethod = 'code';
          window.__authCallbackDebug.code = code.substring(0, 10) + '...';
  

          // Process the code exchange
          console.log('[AuthCallback] Exchanging code for session...');
          const { data, error } = await exchangeCodeForSessionWithRetry(code);

          if (error) {
            console.error('[AuthCallback] Error exchanging code for session:', error);
            window.__authCallbackDebug.codeExchangeError = error;
            throw error;
          }

          if (data?.session?.user) {
            console.log('[AuthCallback] Auth successful with code exchange. User:', data.session.user.id);
            window.__authCallbackDebug.stage = 'session_established';
            window.__authCallbackDebug.userId = data.session.user.id;
            session = data.session;
          } else {
            console.error('[AuthCallback] No user data returned from code exchange');
            window.__authCallbackDebug.stage = 'no_user_data';
            setError('Authentication failed. Please try again.');
            return;
          }
        } else if (accessToken && refreshToken) {
          // --- Handle Token Hash Flow --- 
          console.log('[AuthCallback] Auth with token hash flow detected');
          window.__authCallbackDebug.authMethod = 'token_hash';
          window.__authCallbackDebug.stage = 'setting_session_from_hash';

          console.log('[AuthCallback] Setting session with tokens...');
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            console.error('[AuthCallback] Error setting session with tokens:', error);
            window.__authCallbackDebug.tokenSessionError = error;
            throw error;
          }

          if (data.session?.user) {
            console.log('[AuthCallback] Auth successful with token hash. User:', data.session.user.id);
            window.__authCallbackDebug.stage = 'session_established_from_hash';
            window.__authCallbackDebug.userId = data.session.user.id;
            session = data.session;
          } else {
            console.error('[AuthCallback] No user data returned from token session');
            window.__authCallbackDebug.stage = 'no_user_data_from_hash';
            setError('Authentication failed. Please try again.');
            return;
          }
        } else {
          // Try to check for an existing session directly
          console.log('[AuthCallback] No code or token in URL, checking for existing session');
          window.__authCallbackDebug.stage = 'checking_session_directly';
          window.__authCallbackDebug.authMethod = 'direct_check';

          const allowFallback = hasFreshAuthFlowMarker() && !isSignedOutMarkerActive();
          window.__authCallbackDebug.fallbackAllowed = allowFallback;
          if (!allowFallback) {
            window.__authCallbackDebug.stage = 'stale_auth_flow';
            setError('Your sign-in session expired. Please sign in again.');
            clearAuthFlowMarker();
            navigate('/signin?auth_error=stale', { replace: true });
            return;
          }

          // Force a session check and refresh
          console.log('[AuthCallback] Forcing session check...');
          // Note: Removed checkSessionAndFetchProfile call as it doesn't exist in AuthContext

          // Check if that gave us a session
          const { data: fallbackSessionData } = await supabase.auth.getSession();
          window.__authCallbackDebug.initialSessionCheck = {
            hasSession: !!fallbackSessionData?.session,
            userId: fallbackSessionData?.session?.user?.id
          };
          session = fallbackSessionData?.session || null;

          if (!session?.user) {
            console.error('[AuthCallback] No authentication data found');
            window.__authCallbackDebug.stage = 'no_auth_data';
            setError('Invalid authentication callback. Please try signing in again.');
            return;
          }
        }

        if (!session?.user) {
          console.error('[AuthCallback] Session was not established after auth flow');
          setError('Authentication failed. Please try again.');
          return;
        }

        clearSignedOutAt();
        console.log('[AuthCallback] Session established:', session.user.id);
        window.__authCallbackDebug.userId = session.user.id;
        if (window.__authCallbackDebug.stage === 'checking_session_directly') {
          window.__authCallbackDebug.stage = 'using_existing_session';
        }
        await handleSuccessfulAuth(session);
      } catch (err) {
        console.error('Auth callback error:', err);
        window.__authCallbackDebug.error = err.message || 'Unknown error';
        window.__authCallbackDebug.stage = 'error';
        setError(err.message || 'Authentication failed. Please try again.');
      } finally {
        setLoading(false);
        clearAuthFlowMarker();
        window.__authCallbackDebug.completed = true;
        window.__authCallbackDebug.completedAt = new Date().toISOString();
      }
    };

    // Helper function to handle successful authentication
    const handleSuccessfulAuth = async (session) => {
      try {
        const userId = typeof session === 'string' ? session : session?.user?.id;
        if (!userId) {
          throw new Error('Your session could not be verified. Please try signing in again.');
        }

        console.log(`[AuthCallback] Handling successful auth for user ${userId}`);
        window.__authCallbackDebug.handlingAuth = { userId, timestamp: new Date().toISOString() };

        // User authenticated: Check if they need onboarding before redirect
        console.log('[AuthCallback] User authenticated. Checking profile and onboarding status...');
        window.__authCallbackDebug.stage = 'authenticated_checking_profile';

        window.__authCallbackDebug.profileBootstrap = {
          attempted: false,
          fallbackUsed: false,
          failed: false,
          lastStage: 'not_started'
        };

        const handleProfileBootstrapEvent = (event = {}) => {
          const bootstrapDebug = window.__authCallbackDebug.profileBootstrap || {};
          const nextDebug = {
            ...bootstrapDebug,
            lastStage: event.stage || bootstrapDebug.lastStage || 'unknown'
          };

          if (event.stage === 'create_attempt_started') {
            nextDebug.attempted = true;
          }

          if (event.stage === 'missing_columns_retry') {
            nextDebug.attempted = true;
            nextDebug.fallbackUsed = true;
          }

          if (event.stage === 'profile_creation_failed') {
            nextDebug.failed = true;
          }

          window.__authCallbackDebug.profileBootstrap = nextDebug;
        };

        // Fetch profile and subscription in parallel for speed
        const [profileResult, userSubscription] = await Promise.all([
          refreshProfileCache(userId, {
            onProfileBootstrapEvent: handleProfileBootstrapEvent
          }),
          fetchUserSubscription(userId, { updateState: false })
        ]);
        window.__authCallbackDebug.cacheRefreshed = true;

        let userProfile = profileResult;

        // Fallback: direct DB fetch if profile cache refresh returned null
        if (!userProfile) {
          window.__authCallbackDebug.profileBootstrap = {
            ...window.__authCallbackDebug.profileBootstrap,
            failed: true,
            lastStage: window.__authCallbackDebug.profileBootstrap?.lastStage || 'profile_missing_after_refresh'
          };
          window.__authCallbackDebug.profileFetchFallback = true;
          try {
            const { data: profileData, error: profileError } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', userId)
              .maybeSingle();

            if (!profileError && profileData) {
              userProfile = profileData;
            } else {
              console.error('[AuthCallback] Error fetching profile:', profileError);
            }
          } catch (error) {
            console.error('[AuthCallback] Exception fetching profile:', error);
          }
        }

        // Signal to AuthContext that we just hydrated, so hydrateFromSession can skip re-fetching
        markCallbackHydration(userId, { subscription: userSubscription ?? null });

        // Determine redirect based on shared access rules and intended route
        const intendedRoute = peekIntendedRoute();
        let redirectTo = AUTHENTICATED_HOME_ROUTE;
        let redirectReason = 'authenticated_home';
        let preserveIntendedRoute = false;

        if (userProfile) {
          const resolvedAccess = resolveAccessState({
            user: { id: userId },
            profile: userProfile,
            subscription: userSubscription,
            loading: false,
            profileFresh: true,
            subscriptionFresh: true,
          });

          console.log('[AuthCallback] Profile check:', {
            subscriptionType: userProfile.subscription_type,
            onboardingCompleted: userProfile.onboarding_completed,
            resolvedStatus: resolvedAccess.status,
            subscriptionPlanType: userSubscription?.plan_type || null,
            subscriptionStatus: userSubscription?.status || null,
          });
          console.log('[AuthCallback] Full profile data:', JSON.stringify(userProfile, null, 2));

          if (resolvedAccess.status === 'needs_onboarding') {
            redirectTo = resolvedAccess.redirectTarget || '/onboarding';
            redirectReason = 'needs_onboarding';
            preserveIntendedRoute = Boolean(intendedRoute);
          } else if (!resolvedAccess.hasPaidAccess && resolvedAccess.redirectTarget) {
            redirectTo = resolvedAccess.redirectTarget;
            redirectReason = 'needs_membership';
            preserveIntendedRoute = Boolean(intendedRoute);
          }
        }

        if (!preserveIntendedRoute) {
          consumeIntendedRoute();
        }

        window.__authCallbackDebug.redirectingTo = redirectTo;
        window.__authCallbackDebug.redirectReason = redirectReason;
        window.__authCallbackDebug.finalRedirect = { to: redirectTo, reason: redirectReason };

        // Immediate redirect - AuthContext will handle state updates asynchronously
        console.log('[AuthCallback] Redirecting immediately...');

        setRedirected(true); // Mark as redirected to prevent loops
        if (window.location.pathname !== redirectTo) {
          navigate(redirectTo, { replace: true }); // Use replace to avoid back button issues
        }
      } catch (error) {
        console.error('[AuthCallback] Error in handleSuccessfulAuth:', error);
        window.__authCallbackDebug.finalError = error;
        throw error;
      }
    };

    if (!redirected) {
      handleAuthCallback();
    }
  }, [fetchUserSubscription, markCallbackHydration, navigate, refreshProfileCache, redirected]);

  return (
    <div className="ap-auth-transition fixed inset-0 flex items-center justify-center overflow-hidden">
      <div className="relative z-10 flex flex-col items-center animate-fade-in-up">
        {loading ? (
          <>
            {/* Logo */}
            <span className="text-xl font-bold tracking-tight text-slate-400 mb-10">
              APDoneRight
            </span>

            {/* Pulsing ring spinner */}
            <div className="relative w-16 h-16 mb-8">
              <span className="absolute inset-0 rounded-full border-2 border-blue-400/30 animate-ping-slow" />
              <span className="absolute inset-1 rounded-full border-2 border-blue-400/20 animate-ping-slow" style={{ animationDelay: '0.4s' }} />
              <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-blue-600 animate-spin" />
            </div>

            {/* Stepping status text */}
            <p
              key={statusStep}
              className="ap-transition-status text-sm font-medium text-slate-500 animate-fade-in-up"
              style={{ animationDuration: '0.4s' }}
            >
              {STATUS_MESSAGES[statusStep]}
            </p>
          </>
        ) : error ? (
          <div className="ap-auth-transition-card max-w-sm w-full mx-4 bg-white/80 backdrop-blur-sm border border-slate-200/60 rounded-2xl p-8 shadow-lg">
            <div className="flex flex-col items-center text-center">
              {/* Error icon */}
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-50 to-rose-100 flex items-center justify-center mb-5">
                <svg className="h-7 w-7 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>

              <h2 className="text-xl font-bold text-slate-900 mb-2">
                Something went wrong
              </h2>
              <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                {error}
              </p>

              <div className="flex flex-col gap-3 w-full">
                <button
                  onClick={() => navigate('/signin')}
                  className="ap-transition-primary w-full py-3 px-5 bg-emerald-600 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
                >
                  Try Again
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="w-full py-3 px-5 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-100 transition-colors"
                >
                  Refresh Page
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Success checkmark */}
            <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/25">
              <svg className="h-7 w-7 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="ap-transition-status text-sm font-medium text-emerald-600">
              Redirecting...
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
